/**
 * Slepen met de muis, op de native HTML5-manier.
 *
 * Geen library en geen zelfgebouwde pointer-events. De browser levert de
 * sleepafbeelding, de cursor, het annuleren met Escape en het gedrag als de
 * muis het venster verlaat allemaal gratis; dat namaken kost honderden regels
 * in een client die verder alleen react en react-dom als afhankelijkheid heeft.
 * De enige echte zwakte van HTML5-slepen is touch — en juist daar gebruiken we
 * lang indrukken met een mappenkiezer, dus die zwakte raakt ons niet.
 *
 * Drie eigenaardigheden die je moet kennen als je hieraan werkt:
 *
 * 1. Firefox weigert een sleep te starten als `dragstart` geen `setData()`
 *    doet. Onze eigen MIME-types tellen daarvoor mee.
 * 2. `drop` vuurt alleen als `dragover` `preventDefault()` heeft aangeroepen.
 *    Ongeldige doelen doen dus niets — en krijgen daarmee vanzelf de
 *    "mag niet"-cursor.
 * 3. `setDragImage` heeft een zichtbaar element in het document nodig. Dus
 *    buiten beeld parkeren met `left: -9999px`, niet met `display: none`.
 */

/** Eigen types, zodat een sleep van buiten de app nooit als geldig telt. */
export const MIME_MESSAGES = "application/x-rulido-messages";
export const MIME_FOLDER = "application/x-rulido-folder";

export type DragState =
  | { kind: "messages"; folder: string; uids: number[] }
  | { kind: "folder"; path: string };

/**
 * Bewust géén `text/plain` erbij: dan zou een bericht in elk invoerveld op de
 * pagina als losse tekst gedropt kunnen worden.
 */
export function beginDrag(event: React.DragEvent, state: DragState): void {
  event.dataTransfer.effectAllowed = "move";
  if (state.kind === "messages") {
    event.dataTransfer.setData(MIME_MESSAGES, state.uids.join(","));
  } else {
    event.dataTransfer.setData(MIME_FOLDER, state.path);
  }
}

/** Markeert een doel als geldig; zonder dit komt er geen drop. */
export function acceptDrop(event: React.DragEvent): void {
  event.preventDefault();
  event.dataTransfer.dropEffect = "move";
}

/**
 * Sleepafbeelding voor meerdere berichten. Bij één bericht laten we de browser
 * zijn eigen momentopname van de rij gebruiken — die is mooier dan wat wij
 * kunnen tekenen.
 */
export function setDragGhost(event: React.DragEvent, label: string): void {
  const ghost = document.createElement("div");
  ghost.className = "drag-ghost";
  ghost.textContent = label;
  document.body.append(ghost);
  event.dataTransfer.setDragImage(ghost, 12, 12);
  // Pas weghalen als de browser de afbeelding heeft vastgelegd; direct
  // verwijderen levert een lege sleepafbeelding op.
  requestAnimationFrame(() => ghost.remove());
}
